/**
 * `burn` — the hearth, a spilled pot, a thatch fire, and whatever the
 * otherworld turns out to be carrying.
 *
 * Pain-dominant, near bloodless, and the second-worst thing in the game for
 * infection. A minimal table by design (M1-02): burns reach the head, the
 * torso, the arms and above all the hands, because those are what people put
 * into fires. Nobody in Annwn burns their own foot; when a hazard exists that
 * can do it, the entries go in then.
 */
import { ladder } from './authoring.js';
const BURN = [
    { bleed: 0, shock: 0, pain: 0, impair: 0, knock: 0, infect: 0 },
    { bleed: 0, shock: 1, pain: 2, impair: 0.05, knock: 0, infect: 0.05 },
    { bleed: 0, shock: 2, pain: 4, impair: 0.2, knock: 0, infect: 0.12 },
    { bleed: 1, shock: 3, pain: 7, impair: 0.45, knock: 0, infect: 0.22 },
    { bleed: 2, shock: 5, pain: 10, impair: 0.75, knock: -1, infect: 0.3 },
    { bleed: 3, shock: 7, pain: 13, impair: 1, knock: -2, infect: 0.4 },
];
export const BURN_TABLE = {
    head: ladder(BURN, { shock: 1.3, fx: [[], [], [], [], [], ['kill']] }, [
        ['The heat reddens {victim}’s face.', '{victim} snatches {their} head back from the flame.'],
        [
            'The flame scorches {victim}’s cheek and the skin blisters.',
            '{victim}’s hair catches and is beaten out.',
        ],
        [
            'The fire burns {victim}’s face and the blisters rise in a sheet.',
            'The heat takes {victim}’s brow and lashes.',
        ],
        [
            'The fire burns {victim}’s face down to the raw flesh.',
            'The skin of {victim}’s cheek chars and splits.',
        ],
        [
            'The fire burns away the skin of {victim}’s face and the flesh beneath it.',
            '{victim}’s scalp burns to the bone.',
        ],
        [
            'The fire takes {victim}’s face and {victim} does not rise from it.',
            '{victim} burns, and burns past any help.',
        ],
    ]),
    chest: ladder(BURN, { fx: [[], [], [], [], [], ['kill']] }, [
        [
            'The heat reddens the skin of {victim}’s chest.',
            '{victim}’s shirt smoulders and is beaten out.',
        ],
        ['The flame blisters {victim}’s chest.', 'The fire scorches the skin over {victim}’s ribs.'],
        [
            'The fire burns {victim}’s chest and the blisters rise in a sheet.',
            '{victim}’s shirt burns onto the skin beneath it.',
        ],
        [
            'The fire burns {victim}’s chest down to the raw flesh.',
            'The skin of {victim}’s chest chars and splits.',
        ],
        [
            'The fire burns through the muscle of {victim}’s chest.',
            '{victim}’s chest burns until the breath comes in shreds.',
        ],
        [
            'The fire takes {victim}’s chest and the breathing stops with it.',
            '{victim} burns, and burns past any help.',
        ],
    ]),
    abdomen: ladder(BURN, { infect: 1.5, fx: [[], [], [], [], [], ['kill']] }, [
        ['The heat reddens the skin of {victim}’s belly.', '{victim} twists away from the flame.'],
        ['The flame blisters {victim}’s belly.', 'The fire scorches {victim}’s flank.'],
        [
            'The fire burns {victim}’s belly and the blisters rise in a sheet.',
            '{victim}’s clothing burns onto {their} side.',
        ],
        [
            'The fire burns {victim}’s belly down to the raw flesh.',
            'The skin of {victim}’s side chars and splits.',
        ],
        [
            'The fire burns through the wall of {victim}’s belly.',
            '{victim}’s side burns until the gut lies open to the air.',
        ],
        [
            'The fire takes {victim}’s belly and there is nothing to be done for it.',
            '{victim} burns, and burns past any help.',
        ],
    ]),
    arm: ladder(BURN, {}, [
        [
            'The heat reddens {victim}’s {part}.',
            '{victim} snatches {their} {part} back from the flame.',
        ],
        ['The flame blisters {victim}’s {part}.', 'The fire scorches the skin of {victim}’s {part}.'],
        [
            'The fire burns {victim}’s {part} and the blisters rise in a sheet.',
            '{victim}’s sleeve burns onto {their} {part}.',
        ],
        [
            'The fire burns {victim}’s {part} down to the raw flesh.',
            'The skin of {victim}’s {part} chars and splits.',
        ],
        [
            'The fire burns through the muscle of {victim}’s {part}.',
            '{victim}’s {part} burns until the arm will not straighten again.',
        ],
        [
            'The fire burns {victim}’s {part} to the bone.',
            '{victim}’s {part} is charred past all use of it.',
        ],
    ]),
    hand: ladder(BURN, { pain: 1.2, impair: 1.1 }, [
        ['The heat reddens {victim}’s {part}.', '{victim} snatches {their} {part} out of the flame.'],
        ['The flame blisters {victim}’s palm.', 'The fire scorches the back of {victim}’s {part}.'],
        [
            'The fire burns {victim}’s {part} and the fingers will not close.',
            'The blisters rise across {victim}’s palm in a sheet.',
        ],
        [
            'The fire burns {victim}’s {part} down to the raw flesh.',
            'The skin of {victim}’s {part} chars and peels away.',
        ],
        [
            'The fire burns through the tendons of {victim}’s {part}.',
            '{victim}’s fingers burn and curl in upon themselves.',
        ],
        [
            'The fire burns {victim}’s {part} to the bone.',
            '{victim}’s {part} is charred past all use of it.',
        ],
    ]),
};
//# sourceMappingURL=burn.js.map